import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Card } from '@caruso/ui';
import { venueReservationsService } from '@/services/venue-reservations.service';
import { venuesService } from '@/services/venues.service';
import { ArrowLeft, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { ReservationStatus } from '@caruso/types';
import { cn } from '@caruso/ui';

export function VenueReservationsCalendarPage() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');

  const { data: venues } = useQuery({
    queryKey: ['venues-active'],
    queryFn: () => venuesService.getAll(true),
  });

  // Get first and last day of current month
  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

  const { data: reservations } = useQuery({
    queryKey: ['calendar-reservations', selectedVenueId, currentDate.getMonth(), currentDate.getFullYear()],
    queryFn: () =>
      venueReservationsService.getCalendar(
        selectedVenueId || undefined,
        firstDay.toISOString().split('T')[0],
        lastDay.toISOString().split('T')[0],
      ),
  });

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getMonthDays = () => {
    const days = [];
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    // Previous month days to fill the grid
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
    }

    return days;
  };

  const getReservationsForDay = (date: Date) => {
    if (!reservations) return [];
    return reservations.filter((res) => {
      const resStart = new Date(res.startDate);
      const resEnd = new Date(res.endDate);
      return date >= resStart && date <= resEnd;
    });
  };

  const getStatusColor = (status: ReservationStatus) => {
    const colors = {
      pending: 'bg-yellow-500',
      confirmed: 'bg-green-500',
      cancelled: 'bg-red-500',
      completed: 'bg-blue-500',
    };
    return colors[status];
  };

  const monthDays = getMonthDays();
  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/app/venue-reservations')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-brand-gold">Calendário de Reservas</h1>
            <p className="text-brand-ivory/60 mt-1">Visualize todas as reservas do mês</p>
          </div>
        </div>
        <Button onClick={() => navigate('/app/venue-reservations/new')} variant="premium">
          <Plus className="w-4 h-4 mr-2" />
          Nova Reserva
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-brand-ink/50 border-brand-gold/20 p-4">
        <div className="flex items-center gap-4">
          <label className="text-brand-ivory/70">Filtrar por ambiente:</label>
          <select
            value={selectedVenueId}
            onChange={(e) => setSelectedVenueId(e.target.value)}
            className="flex-1 max-w-md p-2 bg-brand-ink border border-brand-gold/20 rounded text-brand-ivory"
          >
            <option value="">Todos os ambientes</option>
            {venues?.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {/* Calendar */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        {/* Month Navigation */}
        <div className="p-4 border-b border-brand-gold/20 flex items-center justify-between">
          <Button variant="outline" size="icon" onClick={previousMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="text-2xl font-bold text-brand-gold">
            {currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
          </h2>
          <Button variant="outline" size="icon" onClick={nextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Calendar Grid */}
        <div className="p-4">
          {/* Week Days Header */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="text-center text-brand-ivory/60 font-semibold text-sm py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div className="grid grid-cols-7 gap-2">
            {monthDays.map((date, index) => {
              const dayReservations = date ? getReservationsForDay(date) : [];
              const isToday =
                date &&
                date.getDate() === new Date().getDate() &&
                date.getMonth() === new Date().getMonth() &&
                date.getFullYear() === new Date().getFullYear();

              return (
                <div
                  key={index}
                  className={cn(
                    'min-h-[100px] p-2 border border-brand-gold/10 rounded',
                    date ? 'bg-brand-ink/30' : 'bg-brand-ink/10',
                    isToday && 'ring-2 ring-brand-gold',
                  )}
                >
                  {date && (
                    <>
                      <div className="text-sm font-semibold text-brand-ivory/80 mb-1">
                        {date.getDate()}
                      </div>
                      <div className="space-y-1">
                        {dayReservations.slice(0, 3).map((res) => (
                          <div
                            key={res.id}
                            onClick={() => navigate(`/app/venue-reservations/${res.id}`)}
                            className={cn(
                              'text-xs p-1 rounded cursor-pointer hover:opacity-80 truncate',
                              getStatusColor(res.status),
                              'text-white',
                            )}
                            title={`${res.venue?.name} - ${res.customerName}`}
                          >
                            {res.venue?.name}
                          </div>
                        ))}
                        {dayReservations.length > 3 && (
                          <div className="text-xs text-brand-ivory/60 text-center">
                            +{dayReservations.length - 3} mais
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="p-4 border-t border-brand-gold/20 flex gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 rounded" />
            <span className="text-sm text-brand-ivory/70">Pendente</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded" />
            <span className="text-sm text-brand-ivory/70">Confirmada</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded" />
            <span className="text-sm text-brand-ivory/70">Concluída</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded" />
            <span className="text-sm text-brand-ivory/70">Cancelada</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
